"use client";
import React from "react";
import styles from "./RankingQuestion.module.css";
import { InputText } from "@/design-system";
import {
  SELECT_OTHER_LABEL,
  SELECT_OTHER_TEXT_KEY,
  SELECT_OTHER_VALUE,
  decodeOtherTextValue,
  encodeOtherTextValue,
  hasOtherAnswerText,
  isSelectOtherEnabled,
} from "@/lib/selectOtherOption";
import { DEFAULT_MAX_RANK, type RankingConfig, type SurveyQuestion } from "@/types/survey";

interface RankingQuestionProps {
  question: SurveyQuestion;
  brandColor?: string;
  onAnswerChange?: (question: SurveyQuestion, value: string) => void;
  isPreview?: boolean;
  selectedValue?: string | number;
  onPreviewAnswerChange?: (questionId: string, value: string | number) => void;
  /** Non-preview stored value like "opt_1=2;opt_2=1". */
  answerValue?: string;
  uiStrings?: {
    otherOption: string;
    pleaseSpecify: string;
    allRanked: string;
    rankedCount: (ranked: number, total: number) => string;
    pickTop: (count: number) => string;
    eachNumberOnce: string;
    noItemsConfigured: string;
  };
}

/** Number of ranks offered. Uses the configured value, otherwise a fixed
 *  default — never derived from the item count, so the two stay independent. */
const resolveMaxRank = (question: SurveyQuestion): number => {
  const configured = Number((question.ranking as RankingConfig | null)?.maxRank);
  if (Number.isFinite(configured) && configured > 0) return Math.floor(configured);
  return DEFAULT_MAX_RANK;
};

const parseAnswerMap = (raw: unknown): Record<string, string> => {
  const map: Record<string, string> = {};
  if (typeof raw !== "string" || !raw) return map;
  for (const part of raw.split(";")) {
    const separatorIndex = part.indexOf("=");
    if (separatorIndex === -1) continue;
    const k = part.slice(0, separatorIndex);
    const v = part.slice(separatorIndex + 1);
    if (k && v) map[k] = v;
  }
  return map;
};

const serializeAnswerMap = (map: Record<string, string>): string =>
  Object.entries(map)
    .filter(([, v]) => typeof v === "string" && v.length > 0)
    .map(([k, v]) => `${k}=${v}`)
    .join(";");

const otherTextFromMap = (map: Record<string, string>) =>
  decodeOtherTextValue(map[SELECT_OTHER_TEXT_KEY]);

const isRankedEntry = (
  map: Record<string, string>,
  rowKey: string,
  rank: string | undefined,
) => {
  if (rowKey === SELECT_OTHER_TEXT_KEY || !rank) return false;
  if (rowKey === SELECT_OTHER_VALUE) {
    return hasOtherAnswerText(otherTextFromMap(map));
  }
  return true;
};

// Ranking question: respondents assign a unique rank number (1..maxRank)
// to each item (rows come from `choices`). Each number can be held by only
// one item — tapping a number used elsewhere swaps the two.
export default function RankingQuestion({
  question,
  brandColor = "var(--color-alias-info)",
  onAnswerChange,
  isPreview = false,
  selectedValue,
  onPreviewAnswerChange,
  answerValue,
  uiStrings,
}: RankingQuestionProps) {
  const configuredRows = Array.isArray(question.choices) ? question.choices : [];
  const allowOther = isSelectOtherEnabled(question);
  const otherLabel = uiStrings?.otherOption ?? SELECT_OTHER_LABEL;
  const rows = allowOther
    ? [
        ...configuredRows,
        {
          text: otherLabel,
          value: SELECT_OTHER_VALUE,
          isDefault: false,
          createdAt: "",
          updatedAt: "",
        },
      ]
    : configuredRows;
  const maxRank = resolveMaxRank(question);
  const ranks = Array.from({ length: maxRank }, (_, i) => i + 1);

  const currentRaw = isPreview
    ? typeof selectedValue === "string"
      ? selectedValue
      : ""
    : answerValue;
  const valueMap = parseAnswerMap(currentRaw);

  const rowKeyFor = (row: { value?: unknown }, idx: number) =>
    String(row?.value ?? `row_${idx + 1}`);

  const emit = (map: Record<string, string>) => {
    const next = serializeAnswerMap(map);
    if (isPreview) {
      if (onPreviewAnswerChange) onPreviewAnswerChange(question.id, next);
    } else if (onAnswerChange) {
      onAnswerChange(question, next);
    }
  };

  const handleCellClick = (rowKey: string, rank: number, selected: boolean) => {
    const map = parseAnswerMap(currentRaw);
    if (selected) {
      delete map[rowKey];
      if (rowKey === SELECT_OTHER_VALUE) delete map[SELECT_OTHER_TEXT_KEY];
      emit(map);
      return;
    }
    const rankStr = String(rank);
    const previous = map[rowKey];
    const ownerKey = Object.keys(map).find((key) => (
      key !== SELECT_OTHER_TEXT_KEY && map[key] === rankStr
    ));
    map[rowKey] = rankStr;
    if (ownerKey && ownerKey !== rowKey) {
      // Swap when this item already had a rank, otherwise free the owner.
      if (previous) map[ownerKey] = previous;
      else {
        delete map[ownerKey];
        if (ownerKey === SELECT_OTHER_VALUE) delete map[SELECT_OTHER_TEXT_KEY];
      }
    }
    emit(map);
  };

  const handleOtherTextChange = (event: { target: { value: string } }) => {
    const map = parseAnswerMap(currentRaw);
    const next = event.target.value;
    if (next.trim()) {
      map[SELECT_OTHER_TEXT_KEY] = encodeOtherTextValue(next);
    } else {
      delete map[SELECT_OTHER_TEXT_KEY];
    }
    emit(map);
  };

  const rankedCount = Object.entries(valueMap).filter(([rowKey, rank]) =>
    isRankedEntry(valueMap, rowKey, rank),
  ).length;
  const requiredCount =
    configuredRows.length > 0
      ? Math.min(maxRank, configuredRows.length)
      : Math.min(maxRank, rows.length);
  const otherInputValue = otherTextFromMap(valueMap);

  const questionTextStyle = {
    color: "var(--sl-text, var(--color-alias-neutral-darker))",
  };
  const mutedTextStyle = {
    color: "var(--sl-text-muted, var(--color-alias-neutral-dark))",
  };
  const rowLabelStyle = {
    color: "var(--sl-text, var(--color-alias-neutral-darker))",
  };
  const dividerStyle = { borderColor: "var(--color-alias-border-neutral)" };

  const renderChip = (rowKey: string, rank: number) => {
    const selected = valueMap[rowKey] === String(rank);
    const takenByOther =
      !selected &&
      Object.entries(valueMap).some(([key, value]) => (
        key !== SELECT_OTHER_TEXT_KEY && value === String(rank)
      ));
    return (
      <label
        key={`${rowKey}-${rank}`}
        className={`inline-flex items-start justify-center ${styles.cellLabel}`}
        htmlFor={`rank-${question.id}-${rowKey}-${rank}`}
      >
        <input
          id={`rank-${question.id}-${rowKey}-${rank}`}
          type="radio"
          name={`ranking-${question.id}-${rowKey}`}
          value={String(rank)}
          className="sr-only"
          checked={selected}
          onChange={() => handleCellClick(rowKey, rank, false)}
          onClick={(e) => {
            if (selected) {
              e.preventDefault();
              handleCellClick(rowKey, rank, true);
            }
          }}
        />
        <span
          className={`${styles.chip} ${takenByOther ? styles.chipTaken : ""}`}
          style={{
            backgroundColor: selected ? brandColor : "transparent",
            color: selected
              ? "var(--color-alias-foreground-on-color)"
              : "var(--sl-text, var(--color-alias-neutral-darkest))",
            borderColor: selected ? brandColor : "var(--color-alias-neutral-light)",
          }}
        >
          {rank}
        </span>
      </label>
    );
  };

  const renderOtherInput = () => (
    <InputText
      type="text"
      placeholder={uiStrings?.pleaseSpecify ?? "Please specify"}
      value={otherInputValue}
      onChange={handleOtherTextChange}
      fullWidth
    />
  );

  return (
    <div className={`mb-6 ${styles.rankingContainer} text-left`}>
      <div
        className="text-sm font-medium mb-1 text-left w-full"
        style={questionTextStyle}
      >
        {question.text}
      </div>
      {rows.length > 0 ? (
        <div className={`text-xs mb-2 ${styles.caption}`} style={mutedTextStyle}>
          {rankedCount >= requiredCount && requiredCount > 0
            ? uiStrings?.allRanked ?? "All ranked"
            : uiStrings?.rankedCount(rankedCount, requiredCount) ??
              `Ranked ${rankedCount} of ${requiredCount}`}
          {" · "}
          {maxRank < rows.length
            ? uiStrings?.pickTop(maxRank) ?? `Pick your top ${maxRank}`
            : uiStrings?.eachNumberOnce ?? "Each number can be used once"}
        </div>
      ) : null}

      {/* Stacked (mobile) */}
      <div className={styles.rankingStack}>
        {rows.length === 0 ? (
          <div className="text-sm" style={mutedTextStyle}>
            {uiStrings?.noItemsConfigured ?? "No items configured."}
          </div>
        ) : (
          rows.map((row, idx) => {
            const rowKey = rowKeyFor(row, idx);
            return (
              <div key={rowKey} className="border-t pt-2" style={dividerStyle}>
                <div className="text-sm mb-2 text-left" style={rowLabelStyle}>
                  {row?.text}
                </div>
                <div
                  className={`flex items-start flex-wrap gap-2 w-full ${styles.optionsRow}`}
                >
                  {ranks.map((rank) => renderChip(rowKey, rank))}
                </div>
                {rowKey === SELECT_OTHER_VALUE && valueMap[SELECT_OTHER_VALUE] ? (
                  <div className={styles.otherInput}>{renderOtherInput()}</div>
                ) : null}
              </div>
            );
          })
        )}
      </div>

      {/* Table (desktop) */}
      <div className={`${styles.rankingTable} overflow-auto`}>
        <table className="w-full border-collapse">
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  className="text-sm py-2"
                  style={mutedTextStyle}
                  colSpan={ranks.length + 1}
                >
                  {uiStrings?.noItemsConfigured ?? "No items configured."}
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => {
                const rowKey = rowKeyFor(row, idx);
                const rowElement = (
                  <tr key={rowKey} className="border-t" style={dividerStyle}>
                    <td
                      className="text-sm py-2 pr-3 text-left"
                      style={rowLabelStyle}
                    >
                      {row?.text}
                    </td>
                    {ranks.map((rank) => (
                      <td
                        key={`${rowKey}-${rank}`}
                        className={`py-1 px-1 text-center ${styles.optionCell}`}
                      >
                        {renderChip(rowKey, rank)}
                      </td>
                    ))}
                  </tr>
                );
                if (rowKey !== SELECT_OTHER_VALUE || !valueMap[SELECT_OTHER_VALUE]) {
                  return rowElement;
                }
                return (
                  <React.Fragment key={rowKey}>
                    {rowElement}
                    <tr className="border-t" style={dividerStyle}>
                      <td colSpan={ranks.length + 1} className={styles.otherInputCell}>
                        {renderOtherInput()}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
